import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import api from '../services/api';
import { exportarCarteraPDF, exportarCarteraExcel } from '../services/exportar';
import { useEmpresa } from '../context/EmpresaContext';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const formatCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

const estadoConfig = {
  pagado:   { color: '#10B981', bg: 'rgba(16,185,129,0.12)',  label: 'Pagado',   dot: '#10B981' },
  pendiente:{ color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',  label: 'Pendiente',dot: '#F59E0B' },
  vencido:  { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',   label: 'Vencido',  dot: '#EF4444' },
  parcial:  { color: '#A855F7', bg: 'rgba(168,85,247,0.12)',  label: 'Abono parcial', dot: '#A855F7' },
};

export default function Cartera() {
  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [data, setData] = useState({ cobros: [], resumen: {} });
  const [filtroEstado, setFiltroEstado] = useState('');
  const [buscar, setBuscar] = useState('');
  const { empresa } = useEmpresa();
  const [modalPago, setModalPago] = useState(false);
  const [modalGenerar, setModalGenerar] = useState(false);
  const [modalInscripcion, setModalInscripcion] = useState(false);
  const [cobroSeleccionado, setCobroSeleccionado] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [generando, setGenerando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msgGenerar, setMsgGenerar] = useState('');

  const [formPago, setFormPago] = useState({ fecha_pago: hoy.toISOString().split('T')[0], metodo_pago: 'efectivo', monto: '', descuento: 0, motivo_descuento: '', notas: '' });
  const [comprobante, setComprobante] = useState(null);
  const [subiendoComp, setSubiendoComp] = useState(false);

  const METODOS_DIGITALES = ['nequi', 'daviplata', 'transferencia', 'pse'];

  const comprimirImagen = (archivo) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 1280;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve({ base64: canvas.toDataURL('image/jpeg', 0.82).split(',')[1], mime: 'image/jpeg' });
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(archivo);
  });

  const seleccionarComprobante = async (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setSubiendoComp(true);
    try {
      const resultado = await comprimirImagen(archivo);
      setComprobante(resultado);
    } catch { alert('Error al procesar imagen'); }
    finally { setSubiendoComp(false); }
  };

  const [formInscripcion, setFormInscripcion] = useState({ cliente_id: '', monto: '', fecha_pago: hoy.toISOString().split('T')[0], metodo_pago: 'efectivo', notas: '' });

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/api/cartera?mes=${mes}&anio=${anio}`);
      setData(res);
    } catch (e) { console.error(e); }
  }, [mes, anio]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get('/api/clientes').then(r => setClientes(r.data)); }, []);

  const cobros = data.cobros.filter(c => {
    const matchEstado = !filtroEstado || c.estado === filtroEstado;
    const matchBuscar = !buscar || c.cliente_nombre.toLowerCase().includes(buscar.toLowerCase()) || (c.cedula && c.cedula.includes(buscar));
    return matchEstado && matchBuscar;
  });

  const abrirPago = (cobro) => {
    setCobroSeleccionado(cobro);
    setFormPago({ fecha_pago: hoy.toISOString().split('T')[0], metodo_pago: 'efectivo', monto: cobro.monto, descuento: 0, motivo_descuento: '', notas: '' });
    setComprobante(null);
    setModalPago(true);
  };

  const confirmarPago = async () => {
    setGuardando(true);
    try {
      await api.put(`/api/cartera/${cobroSeleccionado.id}/pagar`, {
        ...formPago,
        comprobante_base64: comprobante?.base64 || null,
        comprobante_mime: comprobante?.mime || null,
      });
      setModalPago(false);
      cargar();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  const marcarVencido = async (cobro) => {
    if (!window.confirm(`¿Marcar como vencido el cobro de ${cobro.cliente_nombre}?`)) return;
    try { await api.put(`/api/cartera/${cobro.id}/vencer`); cargar(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  /**
   * Devolverle el servicio a alguien que no ha pagado.
   *
   * Pasa de verdad —«pago el viernes»— y hasta ahora la única forma de reconectarlo era
   * registrar un pago que no existió, dejando la cartera diciendo que entró plata que nadie
   * recibió. Aquí el cobro sigue debiéndose: lo único que cambia es que le vuelve el internet.
   *
   * Se pide el motivo porque es plata que se deja de percibir: sin saber quién lo autorizó ni
   * por qué, un mes después no hay forma de revisarlo.
   */
  const activarSinPago = async (cobro) => {
    const motivo = window.prompt(
      `Reconectar a ${cobro.cliente_nombre} SIN registrar pago.\n\n` +
      'El cobro sigue pendiente y sigue apareciendo como deuda.\n\n' +
      '¿Por qué se reconecta? (queda anotado en el cobro)');
    if (motivo === null) return;
    try {
      const { data } = await api.put(`/api/cartera/${cobro.id}/activar-sin-pago`, { motivo });
      alert(data.mensaje);
      cargar();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const generarCobros = async () => {
    setGenerando(true); setMsgGenerar('');
    try {
      const { data: res } = await api.post('/api/cartera/generar', { mes, anio });
      setMsgGenerar(res.mensaje);
      cargar();
    } catch (e) { setMsgGenerar(e.response?.data?.error || 'Error'); }
    finally { setGenerando(false); }
  };

  const guardarInscripcion = async () => {
    if (!formInscripcion.cliente_id || !formInscripcion.monto) { alert('Cliente y monto son requeridos'); return; }
    setGuardando(true);
    try {
      await api.post('/api/cartera/inscripcion', {
        ...formInscripcion,
        comprobante_base64: comprobante?.base64 || null,
        comprobante_mime: comprobante?.mime || null,
      });
      setModalInscripcion(false);
      setFormInscripcion({ cliente_id: '', monto: '', fecha_pago: hoy.toISOString().split('T')[0], metodo_pago: 'efectivo', notas: '' });
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  };

  const anios = [anio - 1, anio, anio + 1];

  return (
    <Layout>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Cartera</h1>
          <p style={s.sub}>Control de pagos de suscripción mensual</p>
        </div>
        <div style={s.headerBtns}>
          <button style={s.btnExport} onClick={() => exportarCarteraExcel(data.cobros, mes, anio)}>⬇ Excel</button>
          <button style={s.btnExport} onClick={() => exportarCarteraPDF(data.cobros, data.resumen, mes, anio, empresa)}>🖨 PDF</button>
          <button style={s.btnSec} onClick={() => setModalInscripcion(true)}>+ Inscripción</button>
          <button style={s.btnSec} onClick={() => setModalGenerar(true)}>⚡ Generar cobros</button>
        </div>
      </div>

      {/* Selector mes/año */}
      <div style={s.selector}>
        <div style={s.selectorLabel}>Período:</div>
        <div style={s.mesesRow}>
          {MESES.map((m, i) => (
            <button key={i} onClick={() => setMes(i + 1)} style={{ ...s.mesBtn, ...(mes === i + 1 ? s.mesBtnActivo : {}) }}>
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <select style={s.anioSelect} value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
          {anios.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        {[
          { label: 'Total cobros', val: data.resumen.total || 0, color: '#fff', sub: 'clientes' },
          // Los abonos parciales se dicen aparte: si no, un mes con muchos abonos parece
          // que nadie ha pagado, porque solo se contaban los pagos completos.
          { label: 'Cobrado', val: formatCOP(data.resumen.monto_cobrado), color: '#10B981',
            sub: `${data.resumen.pagados || 0} pagos` +
                 (data.resumen.parciales ? ` y ${data.resumen.parciales} abonos` : '') },
          { label: 'Falta por cobrar', val: formatCOP(data.resumen.monto_pendiente), color: '#F59E0B', sub: `${data.resumen.pendientes || 0} sin pagar` },
          { label: 'Vencidos', val: data.resumen.vencidos || 0, color: '#EF4444', sub: 'en mora' },
        ].map(st => (
          <div key={st.label} style={s.statCard}>
            <div style={{ ...s.statVal, color: st.color }}>{st.val}</div>
            <div style={s.statLabel}>{st.label}</div>
            <div style={s.statSub}>{st.sub}</div>
          </div>
        ))}
      </div>

      {/* Estas dos cifras cuentan SOLO la parte de LinkUs, y eso hay que decirlo.
          A los clientes de un revendedor se les cobra la mensualidad entera —y en la lista
          de abajo sale entera, porque es lo que hay que cobrarles— pero de cada una LinkUs
          se queda la tarifa del contrato y el resto es del revendedor. Si el total bajara
          sin explicar por que, lo normal seria pensar que el sistema perdio plata. */}
      {data.resumen.clientes_de_revendedor > 0 && (
        <div style={{ margin: '0 0 16px', padding: '10px 14px', borderRadius: '10px',
                      background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.22)',
                      fontSize: '12.5px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
          <strong style={{ color: '#93C5FD' }}>Cobrado y Falta por cobrar son solo lo de LinkUs.</strong>{' '}
          {data.resumen.clientes_de_revendedor} de estos cobros son de clientes de un revendedor:
          se les cobra la mensualidad completa, pero de cada una LinkUs se queda su tarifa y el
          resto es del revendedor.
          {data.resumen.monto_de_revendedores > 0 && <>
            {' '}De lo recaudado este mes, <strong style={{ color: '#FDBA74' }}>
            {formatCOP(data.resumen.monto_de_revendedores)}</strong> entraron a la caja pero
            son de ellos.
          </>}
        </div>
      )}

      {/* Filtros */}
      <div style={s.filtros}>
        <input style={s.buscador} placeholder="🔍  Buscar cliente..." value={buscar} onChange={e => setBuscar(e.target.value)} />
        {/* «parcial» faltaba: sin él no había forma de sacar la lista de quiénes deben un
            saldo, que son justo a los que hay que volver a cobrar. */}
        {['', 'pendiente', 'vencido', 'parcial', 'pagado'].map(est => (
          <button key={est} onClick={() => setFiltroEstado(est)}
            style={{ ...s.filtroBtn, ...(filtroEstado === est ? s.filtroBtnActivo : {}) }}>
            {est === '' ? 'Todos' : estadoConfig[est].label}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div style={s.tabla}>
        {cobros.length === 0 ? (
          <div style={s.vacio}>
            {data.cobros.length === 0
              ? <div>No hay cobros generados para {MESES[mes-1]} {anio}. <br/><br/><button style={s.btnPrimario} onClick={() => setModalGenerar(true)}>⚡ Generar cobros del mes</button></div>
              : 'No hay resultados con los filtros aplicados'}
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={s.th}>Cliente</th>
                <th style={s.th}>Plan</th>
                <th style={s.th}>Monto</th>
                <th style={s.th}>Día pago</th>
                <th style={s.th}>Estado</th>
                <th style={s.th}>Fecha pago</th>
                <th style={s.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cobros.map(c => {
                const cfg = estadoConfig[c.estado];
                return (
                  <tr key={c.id} style={s.tr}>
                    <td style={s.td}>
                      <div style={s.clienteNombre}>{c.cliente_nombre}</div>
                      {c.cedula && <div style={s.clienteSub}>CC {c.cedula}</div>}
                      {c.telefono && <div style={s.clienteSub}>📞 {c.telefono}</div>}
                      {/* De quien es el cliente. Sin esto, los 6 de Beto se veian igual que
                          los propios y parecia que todo el cobro era de LinkUs. */}
                      {c.revendedor_nombre && (
                        <div style={{ fontSize: '11px', color: '#93C5FD', marginTop: '3px' }}>
                          👥 cliente de {c.revendedor_nombre}
                        </div>
                      )}
                    </td>
                    <td style={s.td}><span style={s.planTag}>{c.plan_nombre || '—'}</span></td>
                    <td style={s.td}>
                      {/* Se le cobra la mensualidad COMPLETA: eso no cambia y por eso es la
                          cifra grande. Debajo, cuanto de esa plata es de LinkUs. */}
                      <span style={s.monto}>{formatCOP(c.monto)}</span>
                      {c.parte_de_linkus != null && (
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                          de LinkUs {formatCOP(c.parte_de_linkus)}
                        </div>
                      )}
                    </td>
                    <td style={s.td}><span style={s.diaPago}>Día {c.dia_pago}</span></td>
                    <td style={s.td}>
                      <span style={{ ...s.estadoBadge, color: cfg.color, background: cfg.bg }}>
                        <span style={{ ...s.dot, background: cfg.dot }} />
                        {cfg.label}
                      </span>
                      {/* La deuda sigue, pero el cliente ya tiene servicio. Sin esta marca la
                          fila se veía idéntica a un vencido cualquiera: se hacía la reconexión
                          y la pantalla no daba ninguna señal de que hubiera pasado algo. */}
                      {c.reconectado_sin_pago && (
                        <div style={s.reconectado}
                          title={(c.notas || '').split('\n').filter(l => l.includes('Reconectado sin pago')).pop() || ''}>
                          🔓 con servicio, sin pagar
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={s.fechaPago}>{c.fecha_pago ? new Date(c.fecha_pago).toLocaleDateString('es-CO') : '—'}</span>
                    </td>
                    <td style={s.td}>
                      <div style={s.acciones}>
                        {c.estado !== 'pagado' && (
                          <button style={s.btnPagar} onClick={() => abrirPago(c)}>✓ Pagar</button>
                        )}
                        {c.estado === 'pendiente' && (
                          <button style={s.btnVencer} onClick={() => marcarVencido(c)}>Vencer</button>
                        )}
                        {/* Solo donde tiene sentido: a un cliente al día no hay nada que
                            reconectarle, y a uno ya pagado tampoco. */}
                        {(c.estado === 'vencido' || c.estado === 'parcial') && (
                          <button style={s.btnSinPago} onClick={() => activarSinPago(c)}
                            title="Le vuelve el internet, pero el cobro sigue pendiente">🔓 Activar sin pagar</button>
                        )}
                        {c.estado === 'pagado' && (
                          <span style={s.pagadoTag}>✓ Registrado</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal Registrar Pago */}
      <Modal abierto={modalPago} onCerrar={() => setModalPago(false)} titulo="Registrar Pago">
        {cobroSeleccionado && (
          <div>
            <div style={s.pagoInfo}>
              <div style={s.pagoCliente}>{cobroSeleccionado.cliente_nombre}</div>
              <div style={s.pagoDetalle}>{MESES[mes-1]} {anio} · {cobroSeleccionado.plan_nombre}</div>
              {cobroSeleccionado.estado === 'parcial' && (
                <div style={{marginTop:'8px', padding:'8px 12px', background:'rgba(168,85,247,0.1)', borderRadius:'8px', fontSize:'13px', color:'#A855F7'}}>
                  Ya abonó {formatCOP(cobroSeleccionado.monto_abonado)} · Saldo pendiente: <strong>{formatCOP(cobroSeleccionado.saldo_pendiente)}</strong>
                </div>
              )}
              {/* Aquí es donde más confunde: se escribe el monto COMPLETO —es lo que el
                  cliente entrega— y sin esta línea parece que los $55.000 son de LinkUs.
                  El reparto no hay que hacerlo a mano: se calcula solo al guardar. */}
              {cobroSeleccionado.revendedor_nombre && (
                <div style={{marginTop:'8px', padding:'8px 12px', background:'rgba(59,130,246,0.1)',
                             borderRadius:'8px', fontSize:'12.5px', color:'rgba(255,255,255,0.75)', lineHeight:1.5}}>
                  👥 Cliente de <strong style={{color:'#93C5FD'}}>{cobroSeleccionado.revendedor_nombre}</strong>.
                  Se le cobra todo, pero de este pago{' '}
                  <strong style={{color:'#6EE7B7'}}>{formatCOP(cobroSeleccionado.parte_de_linkus)}</strong> son de LinkUs
                  y el resto es de él. Se reparte solo.
                </div>
              )}
            </div>
            <div style={s.formCol}>
              <div style={s.campo}><label style={s.label}>Monto recibido ahora</label>
                <input style={s.input} type="number" value={formPago.monto} onChange={e => setFormPago({...formPago, monto: e.target.value})} placeholder="Si es menor al total, queda como abono parcial"/>
                <div style={{fontSize:'11px', color:'rgba(255,255,255,0.4)', marginTop:'4px'}}>
                  Si el monto es menor al saldo, el cobro queda marcado como "Abono parcial" y podrás registrar el resto después.
                </div>
              </div>
              <div style={s.campo}><label style={s.label}>Descuento (monto fijo)</label>
                <input style={s.input} type="number" min="0" value={formPago.descuento} onChange={e => {
                  const desc = parseFloat(e.target.value || 0);
                  const montoOriginal = parseFloat(cobroSeleccionado?.monto || 0);
                  setFormPago({...formPago, descuento: e.target.value, monto: montoOriginal - desc });
                }} placeholder="0" /></div>
              {parseFloat(formPago.descuento) > 0 && (
                <div style={s.campo}><label style={s.label}>Motivo del descuento</label>
                  <input style={s.input} value={formPago.motivo_descuento} onChange={e => setFormPago({...formPago, motivo_descuento: e.target.value})} placeholder="Ej: Falla en el servicio" /></div>
              )}
              <div style={s.campo}><label style={s.label}>Fecha de pago</label>
                <input style={s.input} type="date" value={formPago.fecha_pago} onChange={e => setFormPago({...formPago, fecha_pago: e.target.value})} /></div>
              <div style={s.campo}><label style={s.label}>Método de pago</label>
                <select style={s.input} value={formPago.metodo_pago} onChange={e => setFormPago({...formPago, metodo_pago: e.target.value})}>
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="nequi">Nequi</option>
                  <option value="daviplata">Daviplata</option>
                  <option value="pse">PSE</option>
                  <option value="otro">Otro</option>
                </select></div>
              <div style={s.campo}><label style={s.label}>Notas</label>
                <input style={s.input} value={formPago.notas} onChange={e => setFormPago({...formPago, notas: e.target.value})} placeholder="Observaciones opcionales" /></div>

              {/* Comprobante — solo para pagos digitales */}
              {METODOS_DIGITALES.includes(formPago.metodo_pago) && (
                <div style={s.campo}>
                  <label style={s.label}>📎 Comprobante de pago</label>
                  <label style={{ display:'flex', alignItems:'center', gap:'10px', background:'rgba(0,229,255,0.05)', border:'1px dashed rgba(0,229,255,0.25)', borderRadius:'8px', padding:'10px 14px', cursor:'pointer' }}>
                    <span style={{ color: comprobante ? '#10B981' : '#00E5FF', fontSize:'13px' }}>
                      {subiendoComp ? '⟳ Procesando...' : comprobante ? '✅ Comprobante cargado' : '📷 Seleccionar imagen'}
                    </span>
                    <input type="file" accept="image/*" style={{ display:'none' }} onChange={seleccionarComprobante} />
                  </label>
                  {comprobante && (
                    <img src={`data:${comprobante.mime};base64,${comprobante.base64}`} alt="comprobante" style={{ maxWidth:'100%', maxHeight:'150px', borderRadius:'8px', marginTop:'8px', objectFit:'contain' }} />
                  )}
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSec} onClick={() => setModalPago(false)}>Cancelar</button>
              <button style={s.btnPrimario} onClick={confirmarPago} disabled={guardando}>{guardando ? 'Guardando...' : '✓ Confirmar Pago'}</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Generar Cobros */}
      <Modal abierto={modalGenerar} onCerrar={() => { setModalGenerar(false); setMsgGenerar(''); }} titulo="Generar Cobros del Mes">
        <p style={s.generarInfo}>
          Esto creará un cobro pendiente para cada cliente activo con plan asignado en el período <strong style={{ color: '#fff' }}>{MESES[mes-1]} {anio}</strong>.
          Los cobros ya existentes no serán duplicados.
        </p>
        <div style={{ background:'rgba(0,229,255,0.05)', border:'1px solid rgba(0,229,255,0.15)', borderRadius:'8px', padding:'12px 14px', marginBottom:'14px', fontSize:'13px', color:'rgba(255,255,255,0.6)' }}>
          💡 <strong style={{ color:'#00E5FF' }}>Prorrateo primer mes:</strong> Para clientes nuevos que se instalaron a mitad de mes, usa el botón "Calcular prorrateo" en su perfil de cliente, o registra el cobro directamente desde Cartera con el monto ajustado.
        </div>
        {msgGenerar && <div style={s.msgBox}>{msgGenerar}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => { setModalGenerar(false); setMsgGenerar(''); }}>Cerrar</button>
          <button style={s.btnPrimario} onClick={generarCobros} disabled={generando}>{generando ? 'Generando...' : `⚡ Generar para ${MESES[mes-1]} ${anio}`}</button>
        </div>
      </Modal>

      {/* Modal Inscripción */}
      <Modal abierto={modalInscripcion} onCerrar={() => setModalInscripcion(false)} titulo="Registrar Pago de Inscripción">
        <div style={s.formCol}>
          <div style={s.campo}><label style={s.label}>Cliente *</label>
            <select style={s.input} value={formInscripcion.cliente_id} onChange={e => setFormInscripcion({...formInscripcion, cliente_id: e.target.value})}>
              <option value="">Seleccionar cliente</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select></div>
          <div style={s.campo}><label style={s.label}>Monto *</label>
            <input style={s.input} type="number" value={formInscripcion.monto} onChange={e => setFormInscripcion({...formInscripcion, monto: e.target.value})} placeholder="Valor de inscripción" /></div>
          <div style={s.campo}><label style={s.label}>Fecha</label>
            <input style={s.input} type="date" value={formInscripcion.fecha_pago} onChange={e => setFormInscripcion({...formInscripcion, fecha_pago: e.target.value})} /></div>
          <div style={s.campo}><label style={s.label}>Método de pago</label>
            <select style={s.input} value={formInscripcion.metodo_pago} onChange={e => setFormInscripcion({...formInscripcion, metodo_pago: e.target.value})}>
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="nequi">Nequi</option>
              <option value="daviplata">Daviplata</option>
              <option value="pse">PSE</option>
              <option value="otro">Otro</option>
            </select></div>
          <div style={s.campo}><label style={s.label}>Notas</label>
            <input style={s.input} value={formInscripcion.notas} onChange={e => setFormInscripcion({...formInscripcion, notas: e.target.value})} placeholder="Observaciones" /></div>

          {/* Comprobante inscripción */}
          {METODOS_DIGITALES.includes(formInscripcion.metodo_pago) && (
            <div style={s.campo}>
              <label style={s.label}>📎 Comprobante de pago</label>
              <label style={{ display:'flex', alignItems:'center', gap:'10px', background:'rgba(0,229,255,0.05)', border:'1px dashed rgba(0,229,255,0.25)', borderRadius:'8px', padding:'10px 14px', cursor:'pointer' }}>
                <span style={{ color: comprobante ? '#10B981' : '#00E5FF', fontSize:'13px' }}>
                  {subiendoComp ? '⟳ Procesando...' : comprobante ? '✅ Comprobante cargado' : '📷 Seleccionar imagen'}
                </span>
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={seleccionarComprobante} />
              </label>
              {comprobante && (
                <img src={`data:${comprobante.mime};base64,${comprobante.base64}`} alt="comprobante" style={{ maxWidth:'100%', maxHeight:'150px', borderRadius:'8px', marginTop:'8px', objectFit:'contain' }} />
              )}
            </div>
          )}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => setModalInscripcion(false)}>Cancelar</button>
          <button style={s.btnPrimario} onClick={guardarInscripcion} disabled={guardando}>{guardando ? 'Guardando...' : 'Registrar Inscripción'}</button>
        </div>
      </Modal>
    </Layout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' },
  titulo: { fontSize: '24px', fontWeight: '700', marginBottom: '4px' },
  sub: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  headerBtns: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
  btnPrimario: { background: 'linear-gradient(135deg,#00E5FF,#6366F1)', border: 'none', borderRadius: '10px', padding: '10px 20px', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  btnSec: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '10px 16px', color: '#fff', fontSize: '13px', cursor: 'pointer' },
  btnExport: { background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '10px', padding: '10px 14px', color: '#6EE7B7', fontSize: '13px', cursor: 'pointer', fontWeight: '600' },
  selector: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '12px 16px' },
  selectorLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' },
  mesesRow: { display: 'flex', gap: '4px', flex: 1, flexWrap: 'wrap' },
  mesBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '6px', padding: '4px 10px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  mesBtnActivo: { background: 'rgba(0,229,255,0.15)', border: '1px solid rgba(0,229,255,0.4)', color: '#00E5FF', fontWeight: '600' },
  anioSelect: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '6px 12px', color: '#fff', fontSize: '13px', outline: 'none' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '12px', marginBottom: '20px' },
  statCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px' },
  statVal: { fontSize: '22px', fontWeight: '700' },
  statLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
  statSub: { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' },
  filtros: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px', alignItems: 'center' },
  buscador: { flex: '1 1 220px', minWidth: 0, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '9px 16px', color: '#fff', fontSize: '14px', outline: 'none' },
  filtroBtn: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '7px 14px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', cursor: 'pointer' },
  filtroBtnActivo: { background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.3)', color: '#00E5FF' },
  tabla: { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'rgba(255,255,255,0.04)' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' },
  tr: { borderTop: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '13px 16px', verticalAlign: 'middle' },
  vacio: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '14px', lineHeight: '2' },
  clienteNombre: { fontSize: '14px', fontWeight: '600' },
  clienteSub: { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  planTag: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.06)', padding: '3px 10px', borderRadius: '20px' },
  monto: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  diaPago: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
  estadoBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '20px' },
  dot: { width: '6px', height: '6px', borderRadius: '50%', display: 'inline-block' },
  fechaPago: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
  acciones: { display: 'flex', gap: '6px', alignItems: 'center' },
  btnPagar: { background: 'rgba(16,185,129,0.15)', border: 'none', borderRadius: '6px', padding: '5px 12px', color: '#6EE7B7', fontSize: '12px', cursor: 'pointer', fontWeight: '600' },
  btnVencer: { background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: '6px', padding: '5px 10px', color: '#FCA5A5', fontSize: '12px', cursor: 'pointer' },
  // Ambar, no verde: no es un pago, es una excepción que se concede y que queda debiéndose.
  btnSinPago: { background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '6px', padding: '5px 10px', color: '#FCD34D', fontSize: '12px', cursor: 'pointer' },
  reconectado: { marginTop: '5px', fontSize: '11px', color: '#FCD34D', cursor: 'help' },
  pagadoTag: { fontSize: '12px', color: '#6EE7B7' },
  pagoInfo: { background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' },
  pagoCliente: { fontSize: '16px', fontWeight: '700' },
  pagoDetalle: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' },
  formCol: { display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' },
  campo: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { color: 'rgba(255,255,255,0.6)', fontSize: '13px' },
  input: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', padding: '10px 14px', color: '#fff', fontSize: '14px', outline: 'none' },
  generarInfo: { color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' },
  msgBox: { background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: '8px', padding: '12px', color: '#00E5FF', fontSize: '13px', marginBottom: '16px' },
  modalFooter: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
};
